
import React, { useState, useMemo } from 'react';
import type { FeedbackItem, Unit, Owner, FeedbackReply } from '../../../types';
import { 
    ClockIcon, ArrowPathIcon, CheckCircleIcon,
    WrenchIcon, ShieldCheckIcon, BanknotesIcon, CarIcon,
    ChatBubbleLeftRightIcon, InformationCircleIcon,
    ChevronRightIcon, PhoneIcon, HomeIcon, UserIcon,
    PaperAirplaneIcon
} from '../../ui/Icons';
import { timeAgo } from '../../../utils/helpers';
import BottomSheet from '../../ui/BottomSheet';
import { feedbackAPI } from '../../../services';
import { useNotification, useAuth } from '../../../App';

interface AdminPortalFeedbackPageProps {
    feedback: FeedbackItem[];
    units: Unit[];
    owners: Owner[];
    refreshData: () => void;
}

const statusStyles = {
    Pending: { text: 'Chờ xử lý', classes: 'bg-amber-50 text-amber-600 border-amber-100', icon: <ClockIcon className="w-3.5 h-3.5" /> },
    Processing: { text: 'Đang xử lý', classes: 'bg-blue-50 text-blue-600 border-blue-100', icon: <ArrowPathIcon className="w-3.5 h-3.5" /> },
    Resolved: { text: 'Hoàn tất', classes: 'bg-emerald-50 text-emerald-600 border-emerald-100', icon: <CheckCircleIcon className="w-3.5 h-3.5" /> },
};

const categoryConfig: Record<string, { label: string, icon: React.ReactNode, color: string, bgColor: string }> = {
    maintenance: { label: 'Kỹ thuật', icon: <WrenchIcon className="w-3.5 h-3.5" />, color: 'text-orange-600', bgColor: 'bg-orange-50' },
    security: { label: 'An ninh', icon: <ShieldCheckIcon className="w-3.5 h-3.5" />, color: 'text-red-600', bgColor: 'bg-red-50' },
    hygiene: { label: 'Vệ sinh', icon: <ArrowPathIcon className="w-3.5 h-3.5" />, color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
    billing: { label: 'Hóa đơn', icon: <BanknotesIcon className="w-3.5 h-3.5" />, color: 'text-blue-600', bgColor: 'bg-blue-50' },
    vehicle_reg: { label: 'Gửi xe', icon: <CarIcon className="w-3.5 h-3.5" />, color: 'text-purple-600', bgColor: 'bg-purple-50' },
    general: { label: 'Góp ý', icon: <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" />, color: 'text-slate-600', bgColor: 'bg-slate-50' },
    other: { label: 'Khác', icon: <InformationCircleIcon className="w-3.5 h-3.5" />, color: 'text-gray-600', bgColor: 'bg-gray-50' },
};

const AdminPortalFeedbackPage: React.FC<AdminPortalFeedbackPageProps> = ({ feedback = [], units = [], owners = [], refreshData }) => {
    const { showToast } = useNotification();
    const { user } = useAuth();
    const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
    const [filter, setFilter] = useState<FeedbackItem['status'] | 'All'>('All');
    const [replyText, setReplyText] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const filteredFeedback = useMemo(() => {
        let result = [...feedback];
        if (filter !== 'All') {
            result = result.filter(f => f.status === filter);
        }
        return result.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [feedback, filter]);

    const stats = useMemo(() => ({
        pending: feedback.filter(f => f.status === 'Pending').length,
        processing: feedback.filter(f => f.status === 'Processing').length,
        resolved: feedback.filter(f => f.status === 'Resolved').length,
    }), [feedback]);

    const selectedDetails = useMemo(() => {
        if (!selectedFeedback) return null;
        const unit = units.find(u => u.UnitID === selectedFeedback.residentId);
        const owner = owners.find(o => o.OwnerID === unit?.OwnerID);
        return { unit, owner };
    }, [selectedFeedback, units, owners]);

    const handleReply = async (status: FeedbackItem['status'] = 'Processing') => {
        if (!selectedFeedback || !replyText.trim()) return;

        setIsLoading(true);
        try {
            const newReply: FeedbackReply = {
                author: 'BQL',
                content: replyText.trim(),
                date: new Date().toISOString()
            };

            const updatedReplies = [...(selectedFeedback.replies || []), newReply];
            
            await feedbackAPI.replyFeedback(
                selectedFeedback.id,
                updatedReplies,
                status,
                selectedFeedback.residentId,
                user ? { name: user.DisplayName || 'Admin', email: user.Email || 'admin@system.com' } : undefined
            );

            showToast('Đã gửi phản hồi', 'success');
            setReplyText('');
            refreshData();
            // Update local state to show the new reply immediately in the UI
            setSelectedFeedback({
                ...selectedFeedback,
                replies: updatedReplies,
                status: status
            });
        } catch (error) {
            showToast('Lỗi khi gửi phản hồi', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResolve = async () => {
        if (!selectedFeedback) return;
        if (!window.confirm('Xác nhận đã giải quyết xong phản hồi này?')) return;

        setIsLoading(true);
        try {
            const newReply: FeedbackReply = {
                author: 'BQL',
                content: 'Ban Quản Lý xác nhận đã xử lý xong yêu cầu của bạn. Trân trọng!',
                date: new Date().toISOString()
            };

            const updatedReplies = [...(selectedFeedback.replies || []), newReply];

            await feedbackAPI.replyFeedback(
                selectedFeedback.id,
                updatedReplies,
                'Resolved',
                selectedFeedback.residentId,
                user ? { name: user.DisplayName || 'Admin', email: user.Email || 'admin@system.com' } : undefined
            );

            showToast('Đã đánh dấu hoàn tất', 'success');
            refreshData();
            setSelectedFeedback(null);
        } catch (error) {
            showToast('Lỗi khi cập nhật trạng thái', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-50">
            {/* Stats Header */}
            <div className="p-4 grid grid-cols-3 gap-2 shrink-0 bg-white border-b border-gray-100">
                <button 
                    onClick={() => setFilter(filter === 'Pending' ? 'All' : 'Pending')}
                    className={`p-2 rounded-xl border transition-all flex flex-col items-center gap-1 ${filter === 'Pending' ? 'bg-amber-50 border-amber-200 text-amber-600 ring-2 ring-amber-100' : 'bg-white border-gray-100 text-gray-400'}`}
                >
                    <span className="text-[10px] font-black uppercase tracking-tight">Chờ</span>
                    <span className="text-sm font-black">{stats.pending}</span>
                </button>
                <button 
                    onClick={() => setFilter(filter === 'Processing' ? 'All' : 'Processing')}
                    className={`p-2 rounded-xl border transition-all flex flex-col items-center gap-1 ${filter === 'Processing' ? 'bg-blue-50 border-blue-200 text-blue-600 ring-2 ring-blue-100' : 'bg-white border-gray-100 text-gray-400'}`}
                >
                    <span className="text-[10px] font-black uppercase tracking-tight">Đang xử lý</span>
                    <span className="text-sm font-black">{stats.processing}</span>
                </button>
                <button 
                    onClick={() => setFilter(filter === 'Resolved' ? 'All' : 'Resolved')}
                    className={`p-2 rounded-xl border transition-all flex flex-col items-center gap-1 ${filter === 'Resolved' ? 'bg-emerald-50 border-emerald-200 text-emerald-600 ring-2 ring-emerald-100' : 'bg-white border-gray-100 text-gray-400'}`}
                >
                    <span className="text-[10px] font-black uppercase tracking-tight">Xong</span>
                    <span className="text-sm font-black">{stats.resolved}</span>
                </button>
            </div>

            {/* Feedback List */}
            <div className="p-4 space-y-3 pb-24">
                {filteredFeedback.length > 0 ? filteredFeedback.map(item => {
                    const status = statusStyles[item.status];
                    const category = categoryConfig[item.category] || categoryConfig.other;
                    return (
                        <div 
                            key={item.id} 
                            onClick={() => setSelectedFeedback(item)}
                            className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 active:scale-[0.98] transition-all flex flex-col gap-3"
                        >
                            <div className="flex justify-between items-start">
                                <div className={`flex items-center gap-2 px-2 py-1 rounded-lg ${category.bgColor} ${category.color} border border-current/10`}>
                                    {category.icon}
                                    <span className="text-[9px] font-black uppercase tracking-tight">{category.label}</span>
                                </div>
                                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${status.classes} border border-current/10`}>
                                    {status.icon}
                                    <span className="text-[9px] font-black uppercase tracking-tight">{status.text}</span>
                                </div>
                            </div>
                            
                            <div>
                                <h4 className="text-sm font-black text-gray-800 line-clamp-1 mb-1">{item.subject}</h4>
                                <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{item.content}</p>
                            </div>

                            <div className="flex justify-between items-center pt-2 border-t border-gray-50">
                                <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 bg-gray-50 rounded-lg flex items-center justify-center text-gray-400 border border-gray-100">
                                        <HomeIcon className="w-3.5 h-3.5" />
                                    </div>
                                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{item.residentId} • {timeAgo(item.date)}</span>
                                </div>
                                <ChevronRightIcon className="w-4 h-4 text-gray-200" />
                            </div>
                        </div>
                    );
                }) : (
                    <div className="text-center py-12">
                        <ChatBubbleLeftRightIcon className="w-12 h-12 text-gray-100 mx-auto mb-3" />
                        <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Không có phản hồi nào</p>
                    </div>
                )}
            </div>

            {/* Feedback Detail Bottom Sheet */}
            <BottomSheet 
                isOpen={!!selectedFeedback} 
                onClose={() => {
                    setSelectedFeedback(null);
                    setReplyText('');
                }} 
                title="Chi tiết phản hồi"
            >
                {selectedFeedback && selectedDetails && (
                    <div className="space-y-6 pb-6">
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <div className={`flex items-center gap-2 px-2 py-1 rounded-lg ${categoryConfig[selectedFeedback.category]?.bgColor || 'bg-gray-50'} ${categoryConfig[selectedFeedback.category]?.color || 'text-gray-600'} border border-current/10`}>
                                    {categoryConfig[selectedFeedback.category]?.icon || <InformationCircleIcon className="w-3.5 h-3.5" />}
                                    <span className="text-[9px] font-black uppercase tracking-tight">{categoryConfig[selectedFeedback.category]?.label || 'Khác'}</span>
                                </div>
                                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg ${statusStyles[selectedFeedback.status].classes} border border-current/10`}>
                                    {statusStyles[selectedFeedback.status].icon}
                                    <span className="text-[9px] font-black uppercase tracking-tight">{statusStyles[selectedFeedback.status].text}</span>
                                </div>
                            </div>
                            <h2 className="text-xl font-black text-gray-900 leading-tight">{selectedFeedback.subject}</h2>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{new Date(selectedFeedback.date).toLocaleString('vi-VN')}</p>
                        </div>

                        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 text-gray-700 text-sm leading-relaxed font-medium">
                            {selectedFeedback.content}
                        </div>

                        {/* Media if any */}
                        {selectedFeedback.imageUrl && (
                            <div className="grid grid-cols-1 gap-2">
                                <div className="rounded-2xl overflow-hidden border border-gray-100 aspect-square bg-gray-50">
                                    <img src={selectedFeedback.imageUrl} alt="Feedback" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                </div>
                            </div>
                        )}

                        {/* Replies */}
                        {selectedFeedback.replies && selectedFeedback.replies.length > 0 && (
                            <div className="space-y-3">
                                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Lịch sử phản hồi</h4>
                                <div className="space-y-3">
                                    {selectedFeedback.replies.map((reply, idx) => (
                                        <div 
                                            key={idx}
                                            className={`p-3 rounded-2xl text-xs leading-relaxed ${
                                                reply.author === 'BQL' 
                                                ? 'bg-primary/5 border border-primary/10 ml-6' 
                                                : 'bg-gray-50 border border-gray-100 mr-6'
                                            }`}
                                        >
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-[9px] font-black uppercase tracking-tight text-primary">
                                                    {reply.author === 'BQL' ? 'Ban Quản Lý' : 'Cư dân'}
                                                </span>
                                                <span className="text-[8px] font-bold text-gray-400">
                                                    {new Date(reply.date).toLocaleString('vi-VN')}
                                                </span>
                                            </div>
                                            <p className="font-medium text-gray-700">{reply.content}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Resident Info */}
                        <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                                        <UserIcon className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Người gửi</p>
                                        <p className="text-sm font-black text-gray-800">{selectedDetails.owner?.OwnerName || 'Ẩn danh'}</p>
                                    </div>
                                </div>
                                <a href={`tel:${selectedDetails.owner?.Phone}`} className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl border border-emerald-100">
                                    <PhoneIcon className="w-4 h-4" />
                                </a>
                            </div>
                            <div className="h-px bg-gray-50"></div>
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600">
                                    <HomeIcon className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Căn hộ</p>
                                    <p className="text-sm font-black text-gray-800">{selectedFeedback.residentId}</p>
                                </div>
                            </div>
                        </div>

                        {/* Reply Input */}
                        {selectedFeedback.status !== 'Resolved' && (
                            <div className="space-y-3">
                                <h4 className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Gửi phản hồi</h4>
                                <div className="relative">
                                    <textarea 
                                        rows={3}
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        placeholder="Nhập nội dung phản hồi cho cư dân..."
                                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                                    />
                                    <button 
                                        onClick={() => handleReply()}
                                        disabled={!replyText.trim() || isLoading}
                                        className="absolute bottom-3 right-3 p-2 bg-primary text-white rounded-xl shadow-md active:scale-95 transition-all disabled:opacity-50"
                                    >
                                        <PaperAirplaneIcon className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="grid grid-cols-2 gap-3">
                            <button 
                                onClick={() => handleReply('Processing')}
                                disabled={selectedFeedback.status === 'Resolved' || isLoading}
                                className="flex items-center justify-center gap-2 py-3 bg-primary text-white text-[10px] font-black uppercase tracking-widest rounded-xl shadow-md active:scale-95 transition-all disabled:opacity-50"
                            >
                                <ArrowPathIcon className="w-4 h-4" /> Cập nhật
                            </button>
                            <button 
                                onClick={handleResolve}
                                disabled={selectedFeedback.status === 'Resolved' || isLoading}
                                className="flex items-center justify-center gap-2 py-3 bg-emerald-50 text-emerald-600 text-[10px] font-black uppercase tracking-widest rounded-xl border border-emerald-100 active:scale-95 transition-all disabled:opacity-50"
                            >
                                <CheckCircleIcon className="w-4 h-4" /> Giải quyết
                            </button>
                        </div>
                    </div>
                )}
            </BottomSheet>
        </div>
    );
};

export default AdminPortalFeedbackPage;
